import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import { withFormik } from 'formik';
import { compose, find, propEq, defaultTo, path } from 'ramda';
import Select from 'react-select';
import * as yup from 'yup';

import { apiAdminGetAllCertifications, apiManuallyRegisterForCertification } from '../../../../../api/certification';
import { apiGetEmailDropdownsForCategory } from '../../../../../api/email';
import { isNilOrEmpty } from '../../../../../util/redux';
import { handleSuccess, handleError } from '../../../../../util/forms';
import { routes } from '../../../../../routes';
import magic from '../../../../../util/magic';

import Card from '../../../../../components/Card';
import EditField from '../../../../../components/Forms/EditField';
import Checkbox from '../../../../../components/Forms/Checkbox';

class Registration extends Component {
  static propTypes = {
    user: PropTypes.object,
    updateUser: PropTypes.func,
  };

  state = {
    loaded: false,
    certifications: [],
    exams: [],
    messageDropdown: [],
  };

  constructor(props) {
    super(props);

    apiAdminGetAllCertifications()
      .then(({ data }) => {
        this.setState({ certifications: data.certifications, exams: data.exams });
        return apiGetEmailDropdownsForCategory(magic.emailRegistrationApproval);
      })
      .then(({ data }) => this.setState({ messageDropdown: data.options, loaded: true }));
  }

  render() {
    // TODO: Add military spouse checkbox
    const { values, handleBlur, handleChange, status, setFieldValue, isValid, isSubmitting, handleSubmit } = this.props;
    const { certifications, messageDropdown } = this.state;
    const certification = find(propEq('id', values.certificationId), certifications);
    const pathway = find(propEq('id', values.pathwayTypeId), defaultTo({}, path(['pathwayTypes'], certification)));
    let exams = path(['exams'], pathway);
    if (isNilOrEmpty(exams)) exams = this.state.exams;

    return (
      <Card className="container">
        <Card.Header>Create Registration</Card.Header>
        <Card.Body>
          <div className="offset-md-1">
            <EditField
              as={Select}
              bare
              simpleValue
              name="certificationId"
              label="Certification"
              status={status}
              onChange={(value) => setFieldValue('certificationId', value)}
              options={certifications.map((cert) => ({ value: cert.id, label: cert.name }))}
              value={values.certificationId}
            />
            {certification && (
              <EditField
                as={Select}
                bare
                simpleValue
                name="pathwayTypeId"
                label="Pathway"
                status={status}
                onChange={(value) => setFieldValue('pathwayTypeId', value)}
                options={certification.pathwayTypes.map((path) => ({ value: path.id, label: path.name }))}
                value={values.pathwayTypeId}
              />
            )}
            {pathway && (
              <Fragment>
                <EditField
                  as={Select}
                  bare
                  multi
                  name="exams"
                  label="Exams"
                  status={status}
                  onChange={(value) =>
                    setFieldValue(
                      'exams',
                      value.map((exam) => exam.value),
                    )
                  }
                  options={exams.map((exam) => ({ value: exam.id, label: exam.name }))}
                  value={values.exams}
                />
                <div className="form-group offset-md-3 col-md-4">
                  <Checkbox
                    name="prepaidExams"
                    label="The exams were prepaid"
                    checked={values.prepaidExams}
                    onBlur={handleBlur}
                    onChange={handleChange}
                  />
                </div>
              </Fragment>
            )}
            <EditField
              name="deadline"
              label="Deadline"
              type="date"
              status={status}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.deadline}
            />
            <EditField
              name="graduatedOn"
              label="Graduated On"
              type="date"
              status={status}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.graduatedOn}
            />
            <EditField
              as={Select}
              bare
              multi
              status={status}
              label="Send a customized message to the registrant (optional):"
              name="messageDropdown"
              onChange={(value) =>
                setFieldValue(
                  'selectedMessages',
                  value.map((choice) => choice.value),
                )
              }
              value={values.selectedMessages}
              options={messageDropdown.map((option) => ({ value: option.id, label: option.title }))}
            />
            <EditField
              as="textarea"
              name="message"
              label=" "
              status={status}
              onChange={handleChange}
              onBlur={handleBlur}
              value={values.message}
            />
          </div>
          <div className="form-group row row mb-0 mt-4">
            <div className="col-md-10 text-right">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!isValid || isSubmitting}
                onClick={handleSubmit}
              >
                Add Registration
              </button>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  }
}

export default compose(
  withRouter,
  withFormik({
    handleSubmit: (payload, formProps) => {
      const { props } = formProps;

      apiManuallyRegisterForCertification(payload.certificationId, payload)
        .then(({ data }) => {
          handleSuccess(formProps);
          return data;
        })
        .then((registration) =>
          props.updateUser({ ...props.user, registrations: [...props.user.registrations, registration] }),
        )
        .then(() => props.history.push(routes.admin.users.profile.registrationsBuilder(props.user.id)))
        .catch((err) => handleError(formProps, err));
    },
    mapPropsToValues: ({ user }) => ({
      userId: user.id,
      certificationId: '',
      pathwayTypeId: '',
      exams: [],
      prepaidExams: false,
      deadline: '',
      graduatedOn: '',
      selectedMessages: [],
      message: '',
    }),
    validationSchema: () =>
      yup.object().shape({
        certificationId: yup.number().required(),
        pathwayTypeId: yup.number().required(),
      }),
    isInitialValid: ({ initialValues, validationSchema }) => validationSchema().isValidSync(initialValues),
    validateOnChange: true,
  }),
)(Registration);
